import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft } from 'lucide-react';
import type { ProjectKind } from '@aiai/shared';
import { cancelTransfer, fetchOutgoingTransfers } from '../api';
import { qk } from '../api/queryKeys';
import { formatDateTime } from '../lib/format';
import { transferErrorMessage } from '../lib/transferMessages';
import { useOrg } from '../org/useOrg';
import { Callout } from './Callout';
import { Spinner } from './Spinner';
import { TransferProjectDialog } from './TransferProjectDialog';

/**
 * The danger-zone row for moving a project / prototype to another workspace.
 * Render it for source-workspace owners only (the API is owner-only too).
 * While a request to another owner's workspace is pending it shows that
 * request with Cancel instead of the Transfer button.
 */
export function ProjectTransferZone({
  project,
}: {
  project: { id: string; name: string; kind: ProjectKind; ref: string; factory?: boolean };
}) {
  const queryClient = useQueryClient();
  const { activeOrg } = useOrg();
  const [open, setOpen] = useState(false);
  const noun = project.kind === 'prototype' ? 'prototype' : 'project';

  const outgoingQuery = useQuery({
    queryKey: qk.outgoingTransfers(activeOrg?.id),
    queryFn: fetchOutgoingTransfers,
    enabled: !!activeOrg,
  });
  const pending = (outgoingQuery.data ?? []).find(
    (t) => t.project.id === project.id && t.status === 'pending',
  );

  const cancelMutation = useMutation({
    mutationFn: (transferId: string) => cancelTransfer(transferId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.transfers }),
  });

  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink">Transfer this {noun}</p>
          {pending ? (
            <p className="text-xs text-ink-faint">
              Waiting for <span className="font-medium text-ink">{pending.toOrg.name}</span>{' '}
              <span className="font-mono">({pending.toOrg.slug})</span> to accept — expires{' '}
              {formatDateTime(pending.expiresAt)}.
            </p>
          ) : (
            <p className="text-xs text-ink-faint">
              Move it, with its history, to another workspace you own or to someone else&apos;s.
            </p>
          )}
        </div>
        {pending ? (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => cancelMutation.mutate(pending.id)}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending && <Spinner />}
            Cancel transfer
          </button>
        ) : (
          <button type="button" className="btn-danger-ghost" onClick={() => setOpen(true)}>
            <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden="true" />
            Transfer
          </button>
        )}
      </div>
      {cancelMutation.isError && (
        <Callout tone="danger">
          {transferErrorMessage(cancelMutation.error, 'Could not cancel the transfer')}
        </Callout>
      )}
      <TransferProjectDialog open={open} onClose={() => setOpen(false)} project={project} />
    </div>
  );
}
